import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { AttorneysSectionData } from 'src/dtos/applicationData'
import { handleYesNoAnswers, lookupYearsInPrivatePractice } from 'src/utils/formDataManipulation'
import { aboutAttorneyPosition } from 'src/constants/application'
import { IoMdCheckmark } from 'react-icons/io'
import { TbFileCheck } from 'react-icons/tb'

interface AttorneysSectionProps {
  attorneysSection?: AttorneysSectionData;
  isComplete: boolean;
}

const AttorneysSection: FC<AttorneysSectionProps> = ({ attorneysSection, isComplete }) => {
  const icocAttorneys = attorneysSection?.attorneys?.filter(att => {
    const ic = aboutAttorneyPosition[1]
    const oc = aboutAttorneyPosition[4]

    return (att?.position === ic || att?.position === oc) && !!att?.yearStartedAsICOC
  })

  return (
    <section>
      <header>
        <h4>Attorney</h4>
        {isComplete ? (
          <h6><IoMdCheckmark/> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>ATTORNEYS</SectionArticlesTitle>
      {(attorneysSection?.attorneys && attorneysSection?.attorneys.length > 0) ? 
        attorneysSection?.attorneys?.map((attorney, index) => (
        <article key={index}>
          <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
          <span><h6>Position:</h6> {attorney?.position}</span>
          <span><h6>Email:</h6> {attorney?.emailAddress}</span>
          <span>
            <h6>Years in Private Practice:</h6>
            {
              lookupYearsInPrivatePractice(attorney?.yearsInPrivatePractice)
            }
          </span>
          <span><h6>License in Texas:</h6> {!!attorney?.license?.isLicensedInTexas ? 'Yes' : 'No'}</span>
          {!!attorney?.license?.isLicensedInTexas && (
            <>
              <span><h6>Texas Bar Card Number:</h6> {attorney?.barCardNumber}</span>
              <span><h6>Date Licensed in Texas:</h6> {attorney?.license?.licenseDate}</span>
            </>
          )}
          <span><h6>License in Another State:</h6> {attorney?.license?.isLicensedInOtherState ? 'Yes' : 'No'}</span>
          {!!attorney?.license?.isLicensedInOtherState && (
            <>
              <span><h6>Other State Bar Card Number:</h6> {attorney?.outSideTexasBarCardNumber}</span>
              <span><h6>Another State of License:</h6> {attorney?.license?.otherState}</span>
              <span><h6>License Date in Another State:</h6> {attorney?.license?.dateLicensedInOtherState}</span>
              <span><h6>Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction :</h6> {attorney?.license?.percentageOfWorkSpent}</span>
              <span><h6>License(s) in Another State Info:</h6> {attorney?.license?.licensesInfo}</span>
            </>
          )}
          {!!attorney?.workHistory?.joinedDate && (
            <span><h6>Date joined present firm:</h6> {attorney?.workHistory?.joinedDate}</span>
          )}
          <span><h6>Average hours per week anticipated during the next year for this firm:</h6> {attorney?.workHistory?.hoursPerWeek}</span>
        </article>
      )) : (
        <article>
          <span>N/A</span>
        </article>
      )}
      <SectionArticlesTitle>RELATIONSHIPS</SectionArticlesTitle>
      <article>
        <strong>How many attorneys have left the firm in the past 12 months?</strong>
        <span>{attorneysSection?.attorneysLeftFirmQuantity ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Does any lawyer(s) act as an independent contractor (IC) and/or "Of Counsel" (OC) to your firm or is listed as such on your firm's letterhead or website?</strong>
        <span>{handleYesNoAnswers(attorneysSection?.hasICOCAttorneys)}</span>
      </article>
      <article>
        <strong>Does any firm member act as “Of Counsel” or Independent Contract Attorney to another law firm?</strong>
        <span>{handleYesNoAnswers(attorneysSection?.hasICOCInAnotherFirm)}</span>
        {attorneysSection?.hasICOCInAnotherFirm === true ? (
          <span>{attorneysSection?.hasICOCInAnotherFirmDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Is Applicant a sole practitioner?</strong>
        <span>{handleYesNoAnswers(attorneysSection?.isSolePractitioner)}</span>
        {attorneysSection?.isSolePractitioner === true ? (
          <>
            <br />
            <strong>How many hours does Applicant work on average per week (not including work performed as an Independent Contractor or Of Counsel)?</strong>
            <span><h6>This year: </h6>{attorneysSection?.solePractitionerThisYear ?? 'N/A'}</span>
            <span><h6>Last year: </h6>{attorneysSection?.solePractitionerLastYear ?? 'N/A'}</span>
            <span><h6>Two years ago: </h6>{attorneysSection?.solePractitionerTwoYearsAgo ?? 'N/A'}</span>
            <span><h6>Three years ago: </h6>{attorneysSection?.solePractitionerFourYearsAgo ?? 'N/A'}</span>
            <span><h6>If less than 40 hours, please provide the reason: </h6>{attorneysSection?.reasonLessThanFourtyHours ?? 'N/A'}</span>
          </>
        ) : null}
      </article>
      {attorneysSection?.hasICOCAttorneys === true ? (
        <>
          <SectionArticlesTitle>INDEPENDENT CONTRACTOR/OF COUNSEL</SectionArticlesTitle>
          {icocAttorneys?.map(attorney => (
            <Fragment key={attorney.barCardNumber}>
              <article>
                <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                <span><h6>Year attorney became Independent Contractor or Of Counsel</h6></span>
                <span>{attorney?.yearStartedAsICOC ?? 'N/A'}</span>
                <span><h6>In the past 12 months, how many hours did the Attorney bill on behalf of your firm?</h6></span>
                <span>{attorney?.hoursOnBill ?? 'N/A'}</span>
                <span><h6>
                If the attorney has been acting as Independent Contractor or Of Counsel for your firm for less than 12 months, how many hours do you 
                estimate the attorney will bill on behalf of your firm during their first year?
                </h6></span>
                <span>{attorney?.hoursBilledInFirstYear ?? 'N/A'}</span>
                <span><h6>Does the attorney also practice independently from your firm?</h6></span>
                <span>{handleYesNoAnswers(attorney?.actsIndependentlyFromFirm)}</span>
                {attorney?.actsIndependentlyFromFirm === true ? (
                  <>
                    <span><h6>Where does the attorney practice independently from your firm? </h6></span>
                    <span>{attorney?.actsIndependentlyFromFirmWhere ?? 'N/A'}</span>
                  </>
                ) : null}
                <span><h6>Does the Attorney have professional liability insurance for that independent practice?</h6></span>
                <span>{handleYesNoAnswers(attorney?.hasInsuranceForIndependentPractice)}</span>
                {attorney?.hasInsuranceForIndependentPractice === true ? (
                  <>
                    <span><h6>Name of the insurance company? </h6>{attorney?.insuranceCompanyNameOfIndependent ?? 'N/A'}</span>
                    <span><h6>Effective dates of the policy:</h6></span>
                    <span><h6>From: </h6>{attorney?.effectivePolicyDateFrom ?? 'N/A'}</span>
                    <span><h6>To: </h6>{attorney?.effectivePolicyDateTo ?? 'N/A'}</span>
                    <span><h6>Limits of liability of the policy: </h6>{attorney?.liabilityLimitsOfPolicy ? attorney?.liabilityLimitsOfPolicy : 'N/A'}</span>
                  </>
                ) : null}
                <span><h6>Which of the following tasks does the attorney perform on behalf of your firm?</h6></span>
                {(attorney?.tasksPerformed && attorney?.tasksPerformed.length > 0) ? 
                attorney.tasksPerformed.map(task => (
                  <span key={task}>{task ?? 'N/A'}</span>
                )) : null}
              </article>
            </Fragment>
          ))}
        </>
      ) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{attorneysSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!attorneysSection?.review?.files && attorneysSection?.review?.files.length > 0 ? 
          attorneysSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default AttorneysSection